import { ChangeEvent, useRef, useState } from "react";
import { Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { LogoVariant, uploadLogo } from "@/services/branding";
import { LogoAvatar } from "@/components/shared/LogoAvatar";

interface LogoUploaderProps {
  label: string;
  currentUrl: string | null;
  variant: LogoVariant;
  onUploaded: (url: string) => void;
}

export const LogoUploader = ({
  label,
  currentUrl,
  variant,
  onUploaded,
}: LogoUploaderProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const onSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      const url = await uploadLogo(file, variant);
      onUploaded(url);
      toast.success(`${label} uploaded`);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="flex items-center gap-4">
      <LogoAvatar
        src={currentUrl}
        name={label}
        size="xl"
        className="h-20 w-20 text-3xl font-extrabold rounded-lg"
      />
      <div className="flex-1">
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-muted-foreground">
          PNG, JPEG, SVG, or WebP. Max 2MB.
        </p>
        <div className="mt-2">
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/svg+xml,image/webp"
            className="hidden"
            onChange={onSelect}
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
          >
            <Upload className="mr-2 h-4 w-4" />
            {uploading ? "Uploading…" : "Upload"}
          </Button>
        </div>
      </div>
    </div>
  );
};
